/**
 * THE ASSESSMENT PAGE'S SHARED MODEL (DECISIONS 103 and 104).
 *
 * A teacher writes a one-on-one assessment of ONE dancer: she picks a few
 * specific skills or technique items and writes up to ASSESSMENT_MAX_NOTES_PER_ITEM notes on
 * each. "Limited but more specific": a few items, in depth. The dancer AND
 * her parents read the whole thing, can reply under it, and the author can
 * edit it later, which shows a small "edited" mark.
 *
 * NO IMPORTS, ON PURPOSE, the same reasoning as classSkills.ts: this file is
 * read directly by the client bundle AND by the server lane
 * (netlify/functions/lib/assessmentLane.mjs), so the two can never disagree
 * about what a valid item looks like or how long a note may be.
 *
 * WHERE AN ITEM COMES FROM. Three sources, the studio's own vocabulary first:
 *   'skill'  an entry of the class skills vocabulary (the same word list the
 *            Technique command center and "Skills this level is working on"
 *            use). The server checks the id against the live vocabulary and
 *            stores the vocabulary's own label, never the client's.
 *   'focus'  one of the Tech Program Guide's six core focus areas. Fixed ids,
 *            fixed names (TECHNIQUE_FOCUS_LABELS below).
 *   'custom' a short free-text item, for the one thing neither list names.
 */

export const ASSESSMENT_MAX_ITEMS = 5;
export const ASSESSMENT_MAX_NOTES_PER_ITEM = 12;
export const ASSESSMENT_MAX_NOTE_LENGTH = 2000;
export const ASSESSMENT_MAX_CUSTOM_LABEL_LENGTH = 80;
export const ASSESSMENT_MAX_REPLY_LENGTH = 2000;
export const ASSESSMENT_MAX_REPLIES = 200;

export type AssessmentItemSource = 'skill' | 'focus' | 'custom';

export interface AssessmentItem {
  source: AssessmentItemSource;
  /** The vocabulary id or focus id; '' for a custom item. */
  refId: string;
  label: string;
  notes: string[];
}

/**
 * The Tech Program Guide's six core focus areas, by their fixed ids
 * (techniqueProgram.ts's TECHNIQUE_FOCUS_IDS) and their names as the guide
 * prints them (netlify/functions/data/techniqueProgramGuide2026.mjs).
 */
export const TECHNIQUE_FOCUS_LABELS: Readonly<Record<string, string>> = Object.freeze({
  'placement-alignment': 'Placement & Alignment',
  'feet-ankles': 'Feet & Ankles',
  'balance-control': 'Balance & Control',
  turns: 'Turns',
  'jumps-leaps': 'Jumps & Leaps',
  'flexibility-strength': 'Flexibility & Strength',
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** One line of words, trimmed, inner whitespace kept as typed. */
export function cleanAssessmentText(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.replace(/\r\n?/g, '\n').trim();
  return trimmed.length > 0 && trimmed.length <= max ? trimmed : '';
}

/** A one-line label: whitespace collapsed. */
export function cleanAssessmentLabel(value: unknown, max: number): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.replace(/\s+/g, ' ').trim();
  return trimmed.length > 0 && trimmed.length <= max ? trimmed : '';
}

/**
 * The STRICT reading of a submitted list of items, the one the server gates
 * a save on. `vocabLabelFor` turns a class skill id into the vocabulary's own
 * label, or null when the id is not in the live vocabulary. Returns null when
 * anything at all is wrong, so a bad request is refused whole, never
 * half-saved.
 */
export function sanitizeAssessmentItems(
  raw: unknown,
  vocabLabelFor: (skillId: string) => string | null,
): AssessmentItem[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > ASSESSMENT_MAX_ITEMS) return null;
  const out: AssessmentItem[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (!isRecord(entry)) return null;
    const keys = Object.keys(entry).sort().join(',');
    if (keys !== 'label,notes,refId,source' && keys !== 'notes,refId,source') return null;
    const source = entry.source;
    const refId = typeof entry.refId === 'string' ? entry.refId : null;
    if (refId === null) return null;
    let label: string;
    if (source === 'skill') {
      const found = refId ? vocabLabelFor(refId) : null;
      if (!found) return null;
      label = found;
    } else if (source === 'focus') {
      const found = Object.prototype.hasOwnProperty.call(TECHNIQUE_FOCUS_LABELS, refId)
        ? TECHNIQUE_FOCUS_LABELS[refId] : null;
      if (!found) return null;
      label = found;
    } else if (source === 'custom') {
      if (refId !== '') return null;
      label = cleanAssessmentLabel(entry.label, ASSESSMENT_MAX_CUSTOM_LABEL_LENGTH);
      if (!label) return null;
    } else {
      return null;
    }
    const dedupe = source === 'custom' ? `custom:${label.toLowerCase()}` : `${source}:${refId}`;
    if (seen.has(dedupe)) return null;
    seen.add(dedupe);
    if (!Array.isArray(entry.notes) || entry.notes.length === 0
        || entry.notes.length > ASSESSMENT_MAX_NOTES_PER_ITEM) return null;
    const notes: string[] = [];
    for (const note of entry.notes) {
      const clean = cleanAssessmentText(note, ASSESSMENT_MAX_NOTE_LENGTH);
      if (!clean) return null;
      notes.push(clean);
    }
    out.push({ source, refId, label, notes });
  }
  return out;
}

/** The LENIENT reading of stored or returned items: a malformed row is dropped, never thrown on. */
export function parseAssessmentItems(raw: unknown): AssessmentItem[] {
  if (!Array.isArray(raw)) return [];
  const out: AssessmentItem[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const source = entry.source;
    if (source !== 'skill' && source !== 'focus' && source !== 'custom') continue;
    const label = typeof entry.label === 'string' ? entry.label : '';
    if (!label) continue;
    const notes = Array.isArray(entry.notes)
      ? entry.notes.filter((note): note is string => typeof note === 'string' && note.length > 0)
      : [];
    out.push({ source, refId: typeof entry.refId === 'string' ? entry.refId : '', label, notes });
  }
  return out;
}

export interface AssessmentReply {
  id: string;
  authorKind: 'staff' | 'family' | 'dancer';
  authorName: string;
  text: string;
  createdAtMs: number | null;
  mine: boolean;
}

export interface AssessmentSummary {
  id: string;
  studentId: string;
  studentName: string;
  authorUid: string;
  authorName: string;
  items: AssessmentItem[];
  createdAtMs: number | null;
  editedAtMs: number | null;
  replyCount: number;
  lastReplyAtMs: number | null;
  /** True when the caller wrote it, so the screen offers Edit. */
  canEdit: boolean;
}

export interface AssessmentDetail extends AssessmentSummary {
  replies: AssessmentReply[];
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A summary as the lane returns it, re-typed rather than trusted. */
export function parseAssessmentSummary(raw: unknown): AssessmentSummary | null {
  if (!isRecord(raw) || typeof raw.id !== 'string' || !raw.id) return null;
  return {
    id: raw.id,
    studentId: typeof raw.studentId === 'string' ? raw.studentId : '',
    studentName: typeof raw.studentName === 'string' ? raw.studentName : '',
    authorUid: typeof raw.authorUid === 'string' ? raw.authorUid : '',
    authorName: typeof raw.authorName === 'string' && raw.authorName ? raw.authorName : 'Your teacher',
    items: parseAssessmentItems(raw.items),
    createdAtMs: numberOrNull(raw.createdAtMs),
    editedAtMs: numberOrNull(raw.editedAtMs),
    replyCount: typeof raw.replyCount === 'number' && raw.replyCount >= 0 ? raw.replyCount : 0,
    lastReplyAtMs: numberOrNull(raw.lastReplyAtMs),
    canEdit: raw.canEdit === true,
  };
}

export function parseAssessmentDetail(raw: unknown): AssessmentDetail | null {
  const summary = parseAssessmentSummary(raw);
  if (!summary || !isRecord(raw)) return null;
  const replies: AssessmentReply[] = [];
  if (Array.isArray(raw.replies)) {
    for (const row of raw.replies) {
      if (!isRecord(row) || typeof row.id !== 'string' || typeof row.text !== 'string') continue;
      const kind = row.authorKind === 'family' || row.authorKind === 'dancer' ? row.authorKind : 'staff';
      replies.push({
        id: row.id,
        authorKind: kind,
        authorName: typeof row.authorName === 'string' && row.authorName ? row.authorName : 'Someone',
        text: row.text,
        createdAtMs: numberOrNull(row.createdAtMs),
        mine: row.mine === true,
      });
    }
  }
  return { ...summary, replies };
}

/**
 * Who reads it, said plainly on the teacher's screen while she writes
 * (DECISIONS 103: "it should be clear to the teacher the student and parent
 * will see that notes").
 */
export function assessmentAudienceLine(dancerFirstName: string): string {
  const name = dancerFirstName.trim() || 'This dancer';
  return `${name} and her parents will see these notes, and they can reply.`;
}

/** "Mar 4" style date for a card. */
export function assessmentDateLabel(ms: number | null): string {
  if (ms === null) return '';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The lane's plain codes, turned into a sentence that says what to do. */
export function assessmentErrorMessage(code: string | null, status: number): string {
  switch (code) {
    case 'sign_in_required':
      return 'Your sign-in needs a refresh. Sign out, sign back in, then try again.';
    case 'not_allowed':
      return 'This account cannot do that here.';
    case 'not_your_dancer':
      return 'You can assess dancers in the classes you teach or assist.';
    case 'not_found':
      return 'That assessment is not there any more. Refresh the page.';
    case 'bad_items':
      return 'Pick at least one item, and give every item at least one note.';
    case 'bad_reply':
      return 'Write a reply up to 2,000 characters, then try again.';
    case 'guardian_limit_exceeded':
      return 'This dancer is linked to more grown-ups than one alert can reach. Ask a Director to tidy her family links, then try again.';
    case 'too_many_replies':
      return 'This thread is full. Message the teacher instead.';
    case 'too_many_requests':
      return 'That was a lot in a row. Give it a minute, then try again.';
    case 'origin_invalid':
    case 'missing app check token':
    case 'invalid app check token':
      return 'The Hub could not confirm this page is really the Hub. Refresh it and try again.';
    case 'bad_request':
      return 'Something in that request did not look right. Refresh the page and try again.';
    default:
      break;
  }
  if (status === 0) return 'The Hub could not be reached. Check your connection, then try again.';
  return 'That did not go through. Try again.';
}
